import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Eye, Save, Loader2, ChevronDown, ChevronUp, AlertCircle,
  Brain, FileText, Stethoscope, Activity, ArrowLeft, Sparkles,
  CheckCircle, XCircle, HelpCircle, Search, BookOpen, Printer
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================================================
// COSTANTI E CONFIGURAZIONI
// ============================================================================

const OCCHI = ['OD', 'OS', 'OU'];

const GRADING_LIMITAZIONE = [
  { value: '0', label: 'Normale' },
  { value: '-1', label: '-1 (lieve)' },
  { value: '-2', label: '-2 (moderata)' },
  { value: '-3', label: '-3 (marcata)' },
  { value: '-4', label: '-4 (assente)' }
];

const GRADING_IPERFUNZIONE = [
  { value: '0', label: 'Normale' },
  { value: '+1', label: '+1 (lieve)' },
  { value: '+2', label: '+2 (moderata)' },
  { value: '+3', label: '+3 (marcata)' },
  { value: '+4', label: '+4 (severa)' }
];

const TIPI_FISSAZIONE = [
  { value: 'alternante_mantiene', label: 'Alternante - Mantiene' },
  { value: 'alternante_non_mantiene', label: 'Alternante - Non mantiene' },
  { value: 'preferenza_od', label: 'Preferenza OD' },
  { value: 'preferenza_os', label: 'Preferenza OS' },
  { value: 'fissa_od', label: 'Fissa solo OD' },
  { value: 'fissa_os', label: 'Fissa solo OS' }
];

const TIPI_STRABISMO = [
  { value: 'esotropia', label: 'Esotropia (ET)' },
  { value: 'exotropia', label: 'Exotropia (XT)' },
  { value: 'ipertropia', label: 'Ipertropia (HT)' },
  { value: 'ipotropia', label: 'Ipotropia' },
  { value: 'ortoforia', label: 'Ortoforia' },
  { value: 'esoforia', label: 'Esoforia' },
  { value: 'exoforia', label: 'Exoforia' }
];

const TORSIONE_DISCO = [
  { value: 'normale', label: 'Normale' },
  { value: 'extorsione', label: 'Extorsione' },
  { value: 'intorsione', label: 'Intorsione' },
  { value: 'tilt', label: 'Disco Tiltato (miopia)' }
];

// ============================================================================
// COMPONENTE PRINCIPALE
// ============================================================================

export const VisitaOculistica = () => {
  const { appointmentId } = useParams();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patient_id');
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [patient, setPatient] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [anamnesi, setAnamnesi] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    anamnesi: true,
    refrazione: true,
    tonometria: true,
    motilita: true,
    strabismo: true,
    fundus: true,
    diagnosi: true
  });
  
  // AI States
  const [generatingDiagnosis, setGeneratingDiagnosis] = useState(false);
  const [aiDiagnosis, setAiDiagnosis] = useState(null);
  const [searchingMedisearch, setSearchingMedisearch] = useState(false);
  
  // Form Data
  const [visitData, setVisitData] = useState({
    // Refrazione
    visus_naturale_od: '',
    visus_naturale_os: '',
    visus_corretto_od: '',
    visus_corretto_os: '',
    sfera_od: '',
    sfera_os: '',
    cilindro_od: '',
    cilindro_os: '',
    asse_od: '',
    asse_os: '',
    add_od: '',
    add_os: '',
    
    // Tonometria
    iop_od: '',
    iop_os: '',
    pachimetria_od: '',
    pachimetria_os: '',
    
    // Esame Obiettivo Anteriore
    segmento_anteriore_od: '',
    segmento_anteriore_os: '',
    
    // Motilità Oculare Estrinseca (MOE)
    moe_normale: true,
    lateroversioni_dx: 'normale',
    lateroversioni_sx: 'normale',
    elevazione_od: '0',
    elevazione_os: '0',
    depressione_od: '0',
    depressione_os: '0',
    adduzione_od: '0',
    adduzione_os: '0',
    abduzione_od: '0',
    abduzione_os: '0',
    
    // Strabismo e Cover Test
    tipo_strabismo: '',
    deviazione_lontano_senza_occhiali: '',
    deviazione_vicino_senza_occhiali: '',
    deviazione_lontano_con_occhiali: '',
    deviazione_vicino_con_occhiali: '',
    spct: '', // Simultaneous Prism Cover Test
    spct_maggiore_10dp: null, // true/false/null
    apct_lontano: '', // Alternate Prism Cover Test
    apct_vicino: '',
    fissazione: '',
    head_tilt_test: '', // + OD, + OS, negativo
    head_tilt_compensatorio: '', // dx, sx
    ipertropia_pp: '', // OD, OS
    elevazione_adduzione_od: '0',
    elevazione_adduzione_os: '0',
    depressione_adduzione_od: '0',
    depressione_adduzione_os: '0',
    pattern_v: false,
    pattern_a: false,
    dvd: false,
    nistagmo: false,
    pac: '', // Posizione Anomala del Capo
    
    // Test Speciali
    test_irvine: '', // +, -, non eseguito
    test_lang: '',
    test_worth: '',
    stereopsi: '',
    
    // Diplopia
    diplopia: false,
    diplopia_tipo: '', // orizzontale, verticale, torsionale
    diplopia_distanza: '', // lontano, vicino, entrambe
    diplopia_ad_angolo_corretto: false, // diplopia anche quando strabismo corretto
    stereopsi_ad_angolo_corretto: '', // stereopsi misurata ad angolo corretto
    
    // Fundus Oculi
    fundus_od: '',
    fundus_os: '',
    torsione_disco_od: 'normale',
    torsione_disco_os: 'normale',
    retinografia_commento: '',
    oct_commento: '',
    campo_visivo_commento: '',
    
    // Diagnosi e Terapia
    diagnosi_principale: '',
    diagnosi_secondarie: [],
    diagnosi_differenziali: [],
    terapia_medica: '',
    terapia_chirurgica_indicata: false,
    piano_chirurgico: '',
    note: '',
    prossimo_controllo: ''
  });
  
  // ============================================================================
  // EFFETTI
  // ============================================================================
  
  useEffect(() => {
    loadData();
  }, [appointmentId, patientId]);
  
  const loadData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      // Carica dati paziente e appuntamento
      const promises = [];
      
      if (appointmentId) {
        promises.push(
          axios.get(`${API}/appointments/${appointmentId}`, { 
            headers: { Authorization: `Bearer ${token}` } 
          }).catch(() => null)
        );
      }
      
      if (patientId) {
        promises.push(
          axios.get(`${API}/admin/patients/${patientId}`, { 
            headers: { Authorization: `Bearer ${token}` } 
          }).catch(() => null)
        );
        
        // Carica anamnesi dal chatbot
        promises.push(
          axios.get(`${API}/patient-records/${patientId}`, { 
            headers: { Authorization: `Bearer ${token}` } 
          }).catch(() => null)
        );
        
        // Carica visite precedenti
        promises.push(
          axios.get(`${API}/visits?patient_id=${patientId}&limit=1`, { 
            headers: { Authorization: `Bearer ${token}` } 
          }).catch(() => null)
        );
      }
      
      const results = await Promise.all(promises);
      
      let idx = 0;
      if (appointmentId && results[idx]?.data) {
        setAppointment(results[idx].data);
        idx++;
      }
      
      if (patientId) {
        if (results[idx]?.data?.user) {
          setPatient(results[idx].data.user);
        }
        idx++;
        
        if (results[idx]?.data) {
          setAnamnesi(results[idx].data);
          // Pre-popola alcuni campi dall'anamnesi
          prePopulateFromAnamnesi(results[idx].data);
        }
        idx++;
        
        // Se c'è una visita precedente, pre-popola refrazione
        if (results[idx]?.data?.visits?.[0]) {
          prePopulateFromLastVisit(results[idx].data.visits[0]);
        }
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };
  
  const prePopulateFromAnamnesi = (anamnesisData) => {
    // Pre-popola campi dall'anamnesi raccolta dal chatbot
    if (anamnesisData?.ocular_history) {
      // Logica per pre-popolare in base alla storia oculare
    }
  };
  
  const prePopulateFromLastVisit = (lastVisit) => {
    // Pre-popola refrazione dall'ultima visita
    if (lastVisit) {
      setVisitData(prev => ({
        ...prev,
        sfera_od: lastVisit.sfera_od || prev.sfera_od,
        sfera_os: lastVisit.sfera_os || prev.sfera_os,
        cilindro_od: lastVisit.cilindro_od || prev.cilindro_od,
        cilindro_os: lastVisit.cilindro_os || prev.cilindro_os,
        asse_od: lastVisit.asse_od || prev.asse_od,
        asse_os: lastVisit.asse_os || prev.asse_os
      }));
    }
  };
  
  // ============================================================================
  // HANDLERS
  // ============================================================================
  
  const handleChange = (field, value) => {
    setVisitData(prev => ({ ...prev, [field]: value }));
  };
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  // ============================================================================
  // GENERA DIAGNOSI IA COMPLETA
  // ============================================================================
  
  const generateAIDiagnosis = async () => {
    setGeneratingDiagnosis(true);
    const token = localStorage.getItem('token');
    
    try {
      // Usa sempre l'endpoint di diagnosi completa che analizza TUTTI i dati
      const diagnosisRequest = {
        visit_data: visitData,
        conditions: buildConditionFromVisit(),
        symptoms: buildSymptomsFromVisit(),
        patient_age: patient?.date_of_birth ? calculateAge(patient.date_of_birth) : null,
        patient_type: patient?.date_of_birth && calculateAge(patient.date_of_birth) < 18 ? 'bambino' : 'adulto'
      };
      
      const res = await axios.post(`${API}/medical-ai/diagnosi-completa`, diagnosisRequest, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data?.success) {
        // Se ci sono dati di strabismo, calcola anche il piano operatorio
        let pianoOperatorio = null;
        const spctValue = parseInt(visitData.spct) || parseInt(visitData.apct_lontano) || 0;
        
        if (spctValue >= 10 && visitData.tipo_strabismo) {
          try {
            const pianoRes = await axios.post(`${API}/medical-ai/piano-operatorio`, {
              diagnosi: visitData.tipo_strabismo,
              deviazione_dp: spctValue,
              occhio: visitData.ipertropia_pp || 'OU'
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            if (pianoRes.data?.success) {
              pianoOperatorio = pianoRes.data.piano;
            }
          } catch (e) {
            console.log('Piano operatorio non calcolato:', e);
          }
        }
        
        setAiDiagnosis({
          completa: res.data,
          pianoOperatorio,
          type: 'completa'
        });
        
        toast.success('Diagnosi IA generata con successo!');
      }
      
    } catch (error) {
      console.error('Error generating diagnosis:', error);
      toast.error('Errore nella generazione della diagnosi IA');
    } finally {
      setGeneratingDiagnosis(false);
    }
  };
  
  const buildConditionFromVisit = () => {
    const conditions = [];
    
    // Strabismo
    if (visitData.tipo_strabismo) conditions.push(visitData.tipo_strabismo);
    if (visitData.dvd) conditions.push('DVD (Dissociated Vertical Deviation)');
    if (visitData.nistagmo) conditions.push('nistagmo');
    
    // Valuta pattern
    if (visitData.pattern_v) conditions.push('pattern V');
    if (visitData.pattern_a) conditions.push('pattern A');
    
    // Valuta limitazioni per sindromi specifiche
    if (visitData.abduzione_od !== '0' && visitData.abduzione_od?.startsWith('-')) {
      conditions.push('limitazione abduzione OD');
    }
    if (visitData.abduzione_os !== '0' && visitData.abduzione_os?.startsWith('-')) {
      conditions.push('limitazione abduzione OS');
    }
    if (visitData.elevazione_adduzione_od !== '0' && visitData.elevazione_adduzione_od !== '+1') {
      conditions.push('deficit elevazione in adduzione OD');
    }
    if (visitData.elevazione_adduzione_os !== '0' && visitData.elevazione_adduzione_os !== '+1') {
      conditions.push('deficit elevazione in adduzione OS');
    }
    
    // Fundus e patologie retiniche
    const fundusText = `${visitData.fundus_od} ${visitData.fundus_os} ${visitData.retinografia_commento}`.toLowerCase();
    if (fundusText.includes('maculopatia') || fundusText.includes('drusen') || fundusText.includes('mnv')) {
      conditions.push('maculopatia');
    }
    if (fundusText.includes('neuropatia') || fundusText.includes('neurite')) {
      conditions.push('neuropatia ottica');
    }
    if (fundusText.includes('distacco') || fundusText.includes('rottura')) {
      conditions.push('patologia retinica - URGENTE');
    }
    
    // Glaucoma
    if (fundusText.includes('glaucoma') || fundusText.includes('escavazione') ||
        parseFloat(visitData.iop_od) > 21 || parseFloat(visitData.iop_os) > 21) {
      conditions.push('sospetto glaucoma');
    }
    
    // Vizi refrattivi
    const sferaOD = parseFloat(visitData.sfera_od) || 0;
    const sferaOS = parseFloat(visitData.sfera_os) || 0;
    if (sferaOD < -6 || sferaOS < -6) {
      conditions.push('miopia elevata');
    } else if (sferaOD < 0 || sferaOS < 0) {
      conditions.push('miopia');
    }
    if (sferaOD > 3 || sferaOS > 3) {
      conditions.push('ipermetropia');
    }
    if (visitData.cilindro_od || visitData.cilindro_os) {
      conditions.push('astigmatismo');
    }
    
    // Torsione disco
    if (visitData.torsione_disco_od === 'extorsione' || visitData.torsione_disco_os === 'extorsione') {
      conditions.push('extorsione disco ottico');
    }
    
    // Diplopia
    if (visitData.diplopia) {
      conditions.push('diplopia');
      if (visitData.diplopia_ad_angolo_corretto) {
        conditions.push('diplopia persistente ad angolo corretto');
      }
    }
    
    return conditions.join(', ') || 'visita oculistica generale';
  };
  
  const buildSymptomsFromVisit = () => {
    const symptoms = [];
    
    // PAC
    if (visitData.pac) symptoms.push(`posizione anomala del capo: ${visitData.pac}`);
    if (visitData.head_tilt_compensatorio) {
      symptoms.push(`PAC compensatoria: inclinazione verso ${visitData.head_tilt_compensatorio}`);
    }
    
    // Diplopia
    if (visitData.diplopia) {
      let diplopiaDesc = 'diplopia';
      if (visitData.diplopia_tipo) diplopiaDesc += ` ${visitData.diplopia_tipo}`;
      if (visitData.diplopia_distanza) diplopiaDesc += ` per ${visitData.diplopia_distanza}`;
      symptoms.push(diplopiaDesc);
      
      if (visitData.diplopia_ad_angolo_corretto) {
        symptoms.push('diplopia persiste anche ad angolo corretto');
      }
    }
    
    // Head Tilt Test
    if (visitData.head_tilt_test && visitData.head_tilt_test !== 'negativo') {
      symptoms.push(`Head Tilt Test positivo: ${visitData.head_tilt_test}`);
    }
    
    // Ipertropia
    if (visitData.ipertropia_pp) {
      symptoms.push(`ipertropia ${visitData.ipertropia_pp} in posizione primaria`);
    }
    
    // Deviazioni
    if (visitData.spct) {
      symptoms.push(`SPCT: ${visitData.spct} DP`);
    }
    if (visitData.apct_lontano) {
      symptoms.push(`APCT lontano: ${visitData.apct_lontano} DP`);
    }
    if (visitData.apct_vicino) {
      symptoms.push(`APCT vicino: ${visitData.apct_vicino} DP`);
    }
    
    // Fissazione
    if (visitData.fissazione) {
      symptoms.push(`fissazione: ${visitData.fissazione.replace(/_/g, ' ')}`);
    }
    
    // IOP
    if (parseFloat(visitData.iop_od) > 21) symptoms.push(`IOP elevata OD: ${visitData.iop_od} mmHg`);
    if (parseFloat(visitData.iop_os) > 21) symptoms.push(`IOP elevata OS: ${visitData.iop_os} mmHg`);
    
    // Limitazioni MOE
    if (visitData.lateroversioni_dx === 'limitata') symptoms.push('lateroversione DX limitata');
    if (visitData.lateroversioni_sx === 'limitata') symptoms.push('lateroversione SX limitata');
    
    // Torsione
    if (visitData.torsione_disco_od === 'extorsione') symptoms.push('extorsione disco OD');
    if (visitData.torsione_disco_os === 'extorsione') symptoms.push('extorsione disco OS');
    if (visitData.torsione_disco_od === 'tilt' || visitData.torsione_disco_os === 'tilt') {
      symptoms.push('disco tiltato (miopia)');
    }
    
    // Test sensoriali
    if (visitData.test_irvine === '+') symptoms.push('Test Irvine positivo');
    if (visitData.stereopsi) symptoms.push(`stereopsi: ${visitData.stereopsi}`);
    if (visitData.stereopsi_ad_angolo_corretto) {
      symptoms.push(`stereopsi ad angolo corretto: ${visitData.stereopsi_ad_angolo_corretto}`);
    }
    
    // Elevazione/depressione in adduzione (obliqui)
    if (visitData.elevazione_adduzione_od && visitData.elevazione_adduzione_od !== '0') {
      symptoms.push(`elevazione in adduzione OD: ${visitData.elevazione_adduzione_od}`);
    }
    if (visitData.elevazione_adduzione_os && visitData.elevazione_adduzione_os !== '0') {
      symptoms.push(`elevazione in adduzione OS: ${visitData.elevazione_adduzione_os}`);
    }
    if (visitData.depressione_adduzione_od && visitData.depressione_adduzione_od !== '0') {
      symptoms.push(`limitazione depressione in adduzione OD: ${visitData.depressione_adduzione_od}`);
    }
    if (visitData.depressione_adduzione_os && visitData.depressione_adduzione_os !== '0') {
      symptoms.push(`limitazione depressione in adduzione OS: ${visitData.depressione_adduzione_os}`);
    }
    
    return symptoms;
  };
  
  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };
  
  // ============================================================================
  // RICERCA MEDISEARCH
  // ============================================================================
  
  const searchMedisearch = async (query) => {
    setSearchingMedisearch(true);
    const token = localStorage.getItem('token');
    
    try {
      const res = await axios.post(`${API}/medical-ai/medisearch`, {
        query,
        focus: 'general'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data?.success) {
        setAiDiagnosis(prev => ({
          ...prev,
          medisearch: res.data
        }));
        toast.success('Ricerca MediSearch completata!');
      }
    } catch (error) {
      console.error('MediSearch error:', error);
      toast.error('Errore nella ricerca MediSearch');
    } finally {
      setSearchingMedisearch(false);
    }
  };
  
  // ============================================================================
  // SALVATAGGIO
  // ============================================================================
  
  const saveVisit = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    
    try {
      const visitPayload = {
        patient_id: patientId,
        appointment_id: appointmentId,
        visit_date: new Date().toISOString(),
        ...visitData,
        ai_diagnosis: aiDiagnosis
      };
      
      const res = await axios.post(`${API}/visits/oculistica`, visitPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Visita salvata con successo!');
      
    } catch (error) {
      console.error('Error saving visit:', error);
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };
  
  // ============================================================================
  // RENDERING HELPERS
  // ============================================================================
  
  const SectionHeader = ({ title, icon: Icon, section, badge }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-sky-50 to-white rounded-lg hover:from-sky-100 transition-all"
    >
      <div className="flex items-center gap-3">
        <div className="p-2 bg-sky-100 rounded-lg">
          <Icon className="w-5 h-5 text-sky-700" />
        </div>
        <span className="font-semibold text-sky-800">{title}</span>
        {badge && (
          <span className="px-2 py-0.5 bg-sky-600 text-white text-xs rounded-full">{badge}</span>
        )}
      </div>
      {expandedSections[section] ? (
        <ChevronUp className="w-5 h-5 text-sky-600" />
      ) : (
        <ChevronDown className="w-5 h-5 text-sky-600" />
      )}
    </button>
  );
  
  const InputField = ({ label, field, type = 'text', placeholder, suffix, className = '' }) => (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <div className="relative">
        <input
          type={type}
          value={visitData[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{suffix}</span>
        )}
      </div>
    </div>
  );
  
  const SelectField = ({ label, field, options, className = '' }) => (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <select
        value={visitData[field] || ''}
        onChange={(e) => handleChange(field, e.target.value)}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm bg-white"
      >
        <option value="">Seleziona...</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
  
  const CheckboxField = ({ label, field }) => (
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={visitData[field] || false}
        onChange={(e) => handleChange(field, e.target.checked)}
        className="w-4 h-4 text-sky-600 rounded focus:ring-sky-500"
      />
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );
  
  // ============================================================================
  // RENDER
  // ============================================================================
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#daeef9] via-[#f0f7fc] to-[#d4e8f5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#daeef9] via-[#f0f7fc] to-[#d4e8f5]">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-sky-800">Visita Oculistica</h1>
              {patient && (
                <p className="text-sm text-gray-500">
                  {patient.first_name} {patient.last_name} - {patient.fiscal_code}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button 
              onClick={() => window.print()}
              className="p-2 hover:bg-gray-100 rounded-lg"
              title="Stampa"
            >
              <Printer className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={saveVisit}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salva Visita
            </button>
          </div>
        </div>
      </header>
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Form - 2 columns */}
          <div className="lg:col-span-2 space-y-4">
            
            {/* Anamnesi Pre-compilata */}
            {anamnesi && (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <SectionHeader title="Anamnesi (dal Chatbot)" icon={FileText} section="anamnesi" badge="Pre-compilata" />
                {expandedSections.anamnesi && (
                  <div className="p-4 space-y-3 text-sm">
                    {anamnesi.family_history && (
                      <div>
                        <span className="font-medium text-gray-700">Anamnesi Familiare: </span>
                        <span className="text-gray-600">{JSON.stringify(anamnesi.family_history)}</span>
                      </div>
                    )}
                    {anamnesi.ocular_history && (
                      <div>
                        <span className="font-medium text-gray-700">Anamnesi Oculare: </span>
                        <span className="text-gray-600">{JSON.stringify(anamnesi.ocular_history)}</span>
                      </div>
                    )}
                    {anamnesi.allergies?.length > 0 && (
                      <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg">
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                        <span className="font-medium text-amber-800">Allergie: {anamnesi.allergies.join(', ')}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Refrazione */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <SectionHeader title="Refrazione e Visus" icon={Eye} section="refrazione" />
              {expandedSections.refrazione && (
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center font-medium text-sky-700 bg-sky-50 py-1 rounded">OD</div>
                    <div className="text-center font-medium text-sky-700 bg-sky-50 py-1 rounded">OS</div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <InputField label="Visus Naturale" field="visus_naturale_od" placeholder="10/10" />
                    <InputField label="Visus Naturale" field="visus_naturale_os" placeholder="10/10" />
                    
                    <InputField label="Visus Corretto" field="visus_corretto_od" placeholder="10/10" />
                    <InputField label="Visus Corretto" field="visus_corretto_os" placeholder="10/10" />
                    
                    <InputField label="Sfera" field="sfera_od" type="number" placeholder="-2.50" suffix="D" />
                    <InputField label="Sfera" field="sfera_os" type="number" placeholder="-2.50" suffix="D" />
                    
                    <InputField label="Cilindro" field="cilindro_od" type="number" placeholder="-0.50" suffix="D" />
                    <InputField label="Cilindro" field="cilindro_os" type="number" placeholder="-0.50" suffix="D" />
                    
                    <InputField label="Asse" field="asse_od" type="number" placeholder="180" suffix="°" />
                    <InputField label="Asse" field="asse_os" type="number" placeholder="180" suffix="°" />
                    
                    <InputField label="ADD (Presbiopia)" field="add_od" type="number" placeholder="+2.00" suffix="D" />
                    <InputField label="ADD (Presbiopia)" field="add_os" type="number" placeholder="+2.00" suffix="D" />
                  </div>
                </div>
              )}
            </div>
            
            {/* Tonometria */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <SectionHeader title="Tonometria e Pachimetria" icon={Activity} section="tonometria" />
              {expandedSections.tonometria && (
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <InputField label="IOP OD" field="iop_od" type="number" placeholder="15" suffix="mmHg" />
                    <InputField label="IOP OS" field="iop_os" type="number" placeholder="15" suffix="mmHg" />
                    
                    <InputField label="Pachimetria OD" field="pachimetria_od" type="number" placeholder="550" suffix="μm" />
                    <InputField label="Pachimetria OS" field="pachimetria_os" type="number" placeholder="550" suffix="μm" />
                  </div>
                  
                  {(parseFloat(visitData.iop_od) > 21 || parseFloat(visitData.iop_os) > 21) && (
                    <div className="mt-3 p-3 bg-red-50 rounded-lg flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                      <span className="text-sm text-red-700 font-medium">
                        ⚠️ IOP elevata! Considerare approfondimento per glaucoma.
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Motilità Oculare */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <SectionHeader title="Motilità Oculare Estrinseca (MOE)" icon={Eye} section="motilita" />
              {expandedSections.motilita && (
                <div className="p-4 space-y-4">
                  <CheckboxField label="MOE nella norma" field="moe_normale" />
                  
                  {!visitData.moe_normale && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <SelectField 
                          label="Lateroversione DX" 
                          field="lateroversioni_dx" 
                          options={[
                            { value: 'normale', label: 'Normale' },
                            { value: 'limitata', label: 'Limitata' },
                            { value: 'assente', label: 'Assente' }
                          ]} 
                        />
                        <SelectField 
                          label="Lateroversione SX" 
                          field="lateroversioni_sx" 
                          options={[
                            { value: 'normale', label: 'Normale' },
                            { value: 'limitata', label: 'Limitata' },
                            { value: 'assente', label: 'Assente' }
                          ]} 
                        />
                      </div>
                      
                      <div className="border-t pt-4">
                        <p className="text-sm font-medium text-gray-700 mb-3">Versioni (Limitazioni/Iperfunzioni)</p>
                        <div className="grid grid-cols-2 gap-4">
                          <SelectField label="Elevazione OD" field="elevazione_od" options={GRADING_LIMITAZIONE} />
                          <SelectField label="Elevazione OS" field="elevazione_os" options={GRADING_LIMITAZIONE} />
                          
                          <SelectField label="Depressione OD" field="depressione_od" options={GRADING_LIMITAZIONE} />
                          <SelectField label="Depressione OS" field="depressione_os" options={GRADING_LIMITAZIONE} />
                          
                          <SelectField label="Adduzione OD" field="adduzione_od" options={GRADING_LIMITAZIONE} />
                          <SelectField label="Adduzione OS" field="adduzione_os" options={GRADING_LIMITAZIONE} />
                          
                          <SelectField label="Abduzione OD" field="abduzione_od" options={GRADING_LIMITAZIONE} />
                          <SelectField label="Abduzione OS" field="abduzione_os" options={GRADING_LIMITAZIONE} />
                        </div>
                      </div>
                      
                      <div className="border-t pt-4">
                        <p className="text-sm font-medium text-gray-700 mb-3">Elevazione/Depressione in Adduzione (Obliqui)</p>
                        <div className="grid grid-cols-2 gap-4">
                          <SelectField label="Elevazione in ADD OD" field="elevazione_adduzione_od" options={GRADING_IPERFUNZIONE} />
                          <SelectField label="Elevazione in ADD OS" field="elevazione_adduzione_os" options={GRADING_IPERFUNZIONE} />
                          
                          <SelectField label="Depressione in ADD OD" field="depressione_adduzione_od" options={GRADING_LIMITAZIONE} />
                          <SelectField label="Depressione in ADD OS" field="depressione_adduzione_os" options={GRADING_LIMITAZIONE} />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            
            {/* Strabismo e Cover Test */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <SectionHeader title="Strabismo e Cover Test" icon={Stethoscope} section="strabismo" />
              {expandedSections.strabismo && (
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <SelectField label="Tipo Strabismo" field="tipo_strabismo" options={TIPI_STRABISMO} />
                    <SelectField label="Fissazione" field="fissazione" options={TIPI_FISSAZIONE} />
                  </div>
                  
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">Cover Test / Prismi</p>
                    <div className="grid grid-cols-2 gap-4">
                      <InputField label="Deviazione Lontano (senza occhiali)" field="deviazione_lontano_senza_occhiali" placeholder="15 ET" suffix="DP" />
                      <InputField label="Deviazione Vicino (senza occhiali)" field="deviazione_vicino_senza_occhiali" placeholder="20 ET" suffix="DP" />
                      
                      <InputField label="Deviazione Lontano (con occhiali)" field="deviazione_lontano_con_occhiali" placeholder="10 ET" suffix="DP" />
                      <InputField label="Deviazione Vicino (con occhiali)" field="deviazione_vicino_con_occhiali" placeholder="15 ET" suffix="DP" />
                    </div>
                  </div>
                  
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">SPCT / APCT</p>
                    <div className="grid grid-cols-3 gap-4">
                      <InputField label="SPCT" field="spct" type="number" placeholder="15" suffix="DP" />
                      <InputField label="APCT Lontano" field="apct_lontano" type="number" placeholder="20" suffix="DP" />
                      <InputField label="APCT Vicino" field="apct_vicino" type="number" placeholder="25" suffix="DP" />
                    </div>
                    
                    <div className="mt-3 flex items-center gap-4">
                      <span className="text-sm text-gray-600">SPCT ≥ 10 DP?</span>
                      <label className="flex items-center gap-1">
                        <input 
                          type="radio" 
                          name="spct_check" 
                          checked={visitData.spct_maggiore_10dp === true}
                          onChange={() => handleChange('spct_maggiore_10dp', true)}
                        />
                        <span className="text-sm">Sì</span>
                      </label>
                      <label className="flex items-center gap-1">
                        <input 
                          type="radio" 
                          name="spct_check" 
                          checked={visitData.spct_maggiore_10dp === false}
                          onChange={() => handleChange('spct_maggiore_10dp', false)}
                        />
                        <span className="text-sm">No</span>
                      </label>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">Head Tilt Test e PAC</p>
                    <div className="grid grid-cols-2 gap-4">
                      <SelectField 
                        label="Head Tilt Test (Bielschowsky)" 
                        field="head_tilt_test" 
                        options={[
                          { value: 'negativo', label: 'Negativo' },
                          { value: '+ OD', label: '+ verso spalla DX (deficit OS OD)' },
                          { value: '+ OS', label: '+ verso spalla SX (deficit OS OS)' }
                        ]} 
                      />
                      <SelectField 
                        label="PAC Compensatoria" 
                        field="head_tilt_compensatorio" 
                        options={[
                          { value: '', label: 'Nessuna' },
                          { value: 'dx', label: 'Inclina verso DX' },
                          { value: 'sx', label: 'Inclina verso SX' },
                          { value: 'ruota_dx', label: 'Ruota verso DX' },
                          { value: 'ruota_sx', label: 'Ruota verso SX' }
                        ]} 
                      />
                      
                      <SelectField 
                        label="Ipertropia in PP" 
                        field="ipertropia_pp" 
                        options={[
                          { value: '', label: 'Nessuna' },
                          { value: 'OD', label: 'OD (occhio destro più alto)' },
                          { value: 'OS', label: 'OS (occhio sinistro più alto)' }
                        ]} 
                      />
                      <InputField label="PAC (descrizione)" field="pac" placeholder="Torcicollo dx, mento elevato..." />
                    </div>
                  </div>
                  
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">Pattern e Caratteristiche</p>
                    <div className="flex flex-wrap gap-4">
                      <CheckboxField label="Pattern V" field="pattern_v" />
                      <CheckboxField label="Pattern A" field="pattern_a" />
                      <CheckboxField label="DVD" field="dvd" />
                      <CheckboxField label="Nistagmo" field="nistagmo" />
                    </div>
                  </div>
                  
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">Test Speciali</p>
                    <div className="grid grid-cols-4 gap-4">
                      <SelectField 
                        label="Test di Irvine" 
                        field="test_irvine" 
                        options={[
                          { value: '', label: 'Non eseguito' },
                          { value: '+', label: 'Positivo (+)' },
                          { value: '-', label: 'Negativo (-)' }
                        ]} 
                      />
                      <InputField label="Test di Lang" field="test_lang" placeholder="Positivo/Negativo" />
                      <InputField label="Test di Worth" field="test_worth" placeholder="4 luci / soppressione" />
                      <InputField label="Stereopsi" field="stereopsi" placeholder="40 sec arc" />
                    </div>
                  </div>
                  
                  {/* Sezione Diplopia */}
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">Diplopia</p>
                    <div className="space-y-3">
                      <CheckboxField label="Diplopia presente" field="diplopia" />
                      
                      {visitData.diplopia && (
                        <div className="ml-6 space-y-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <div className="grid grid-cols-2 gap-4">
                            <SelectField 
                              label="Tipo Diplopia" 
                              field="diplopia_tipo" 
                              options={[
                                { value: '', label: 'Seleziona...' },
                                { value: 'orizzontale', label: 'Orizzontale' },
                                { value: 'verticale', label: 'Verticale' },
                                { value: 'torsionale', label: 'Torsionale' },
                                { value: 'mista', label: 'Mista' }
                              ]} 
                            />
                            <SelectField 
                              label="Distanza" 
                              field="diplopia_distanza" 
                              options={[
                                { value: '', label: 'Seleziona...' },
                                { value: 'lontano', label: 'Solo per lontano' },
                                { value: 'vicino', label: 'Solo per vicino' },
                                { value: 'entrambe', label: 'Lontano e vicino' }
                              ]} 
                            />
                          </div>
                          
                          <div className="pt-2 border-t border-amber-300">
                            <CheckboxField label="Diplopia persiste anche ad angolo corretto (con prismi)" field="diplopia_ad_angolo_corretto" />
                            
                            {visitData.diplopia_ad_angolo_corretto && (
                              <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                                <p className="text-xs text-red-700 font-medium">
                                  ⚠️ Diplopia persistente ad angolo corretto: possibile indicazione per correzione prismatica permanente o ritocco chirurgico
                                </p>
                              </div>
                            )}
                          </div>
                          
                          <InputField 
                            label="Stereopsi ad angolo corretto" 
                            field="stereopsi_ad_angolo_corretto" 
                            placeholder="Es: 60 sec arc con prismi" 
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Fundus */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <SectionHeader title="Fundus Oculi" icon={Eye} section="fundus" />
              {expandedSections.fundus && (
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Fundus OD</label>
                      <textarea
                        value={visitData.fundus_od}
                        onChange={(e) => handleChange('fundus_od', e.target.value)}
                        placeholder="Papilla rosea, margini netti, C/D 0.3, macula nella norma, vasi regolari..."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 text-sm h-24"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Fundus OS</label>
                      <textarea
                        value={visitData.fundus_os}
                        onChange={(e) => handleChange('fundus_os', e.target.value)}
                        placeholder="Papilla rosea, margini netti, C/D 0.3, macula nella norma, vasi regolari..."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 text-sm h-24"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <SelectField label="Torsione Disco OD" field="torsione_disco_od" options={TORSIONE_DISCO} />
                    <SelectField label="Torsione Disco OS" field="torsione_disco_os" options={TORSIONE_DISCO} />
                  </div>
                  
                  {(visitData.torsione_disco_od === 'extorsione' || visitData.torsione_disco_os === 'extorsione') && (
                    <div className="p-3 bg-blue-50 rounded-lg flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-blue-600" />
                      <span className="text-sm text-blue-700">
                        Extorsione rilevata: compatibile con deficit Grande Obliquo Superiore
                      </span>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Commento Retinografia</label>
                    <textarea
                      value={visitData.retinografia_commento}
                      onChange={(e) => handleChange('retinografia_commento', e.target.value)}
                      placeholder="Descrivere alterazioni retinografia: drusen, MNV, neuropatia, ecc..."
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 text-sm h-20"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Commento OCT</label>
                      <textarea
                        value={visitData.oct_commento}
                        onChange={(e) => handleChange('oct_commento', e.target.value)}
                        placeholder="Profilo maculare, spessore RNFL, GCC..."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 text-sm h-20"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Commento Campo Visivo</label>
                      <textarea
                        value={visitData.campo_visivo_commento}
                        onChange={(e) => handleChange('campo_visivo_commento', e.target.value)}
                        placeholder="Scotomi, difetti arciformi, riduzione sensibilità..."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 text-sm h-20"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Diagnosi e Terapia */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <SectionHeader title="Diagnosi e Terapia" icon={Brain} section="diagnosi" />
              {expandedSections.diagnosi && (
                <div className="p-4 space-y-4">
                  {/* Bottone Genera Diagnosi IA */}
                  <button
                    onClick={generateAIDiagnosis}
                    disabled={generatingDiagnosis}
                    className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg"
                  >
                    {generatingDiagnosis ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Analisi in corso...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        🤖 Genera Suggerimento Diagnosi con IA
                      </>
                    )}
                  </button>
                  
                  {/* Risultato IA */}
                  {aiDiagnosis && (
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
                      <div className="flex items-center gap-2 mb-3">
                        <Brain className="w-5 h-5 text-purple-600" />
                        <span className="font-semibold text-purple-800">Suggerimento IA PRIME VISION</span>
                      </div>
                      
                      {/* Diagnosi Completa */}
                      {aiDiagnosis.type === 'completa' && aiDiagnosis.completa && (
                        <div className="space-y-3">
                          <div className="p-3 bg-white rounded-lg">
                            <div 
                              className="prose prose-sm max-w-none text-gray-700"
                              style={{ whiteSpace: 'pre-wrap' }}
                            >
                              {aiDiagnosis.completa.diagnosi_completa}
                            </div>
                          </div>
                          
                          {/* Piano Operatorio se calcolato */}
                          {aiDiagnosis.pianoOperatorio && (
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <p className="font-medium text-blue-800 mb-2">🔪 Piano Operatorio Calcolato (Tabelle PRIME VISION):</p>
                              <p className="text-sm text-blue-700">
                                Diagnosi: {aiDiagnosis.pianoOperatorio.diagnosi} | Deviazione: {aiDiagnosis.pianoOperatorio.deviazione_dp} DP
                              </p>
                              {aiDiagnosis.pianoOperatorio.piano_operatorio?.map((step, i) => (
                                <div key={i} className="mt-2 p-2 bg-white rounded">
                                  <p className="font-medium text-sm">{step.procedura || step.nota}</p>
                                  {step.OD && <p className="text-xs text-gray-600">OD: {step.OD}</p>}
                                  {step.OS && <p className="text-xs text-gray-600">OS: {step.OS}</p>}
                                  {step.occhio && <p className="text-xs text-gray-600">Occhio: {step.occhio}</p>}
                                  {step.mm && <p className="text-xs text-gray-600">Mm: {step.mm}</p>}
                                </div>
                              ))}
                              {aiDiagnosis.pianoOperatorio.avvertenze?.length > 0 && (
                                <div className="mt-3 p-2 bg-amber-50 rounded border border-amber-200">
                                  <p className="text-xs text-amber-800 font-medium">⚠️ Avvertenze:</p>
                                  {aiDiagnosis.pianoOperatorio.avvertenze.map((avv, i) => (
                                    <p key={i} className="text-xs text-amber-700">• {avv}</p>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Vecchio tipo strabismo (backward compatibility) */}
                      {aiDiagnosis.type === 'strabismo' && aiDiagnosis.strabismo && (
                        <div className="space-y-3">
                          <div className="p-3 bg-white rounded-lg">
                            <p className="font-medium text-gray-800">
                              📋 Diagnosi: {aiDiagnosis.strabismo.diagnosi_principale}
                            </p>
                            {aiDiagnosis.strabismo.lato_deficit && (
                              <p className="text-sm text-gray-600">
                                Lato deficit: {aiDiagnosis.strabismo.lato_deficit}
                              </p>
                            )}
                          </div>
                          
                          {aiDiagnosis.strabismo.spiegazione?.length > 0 && (
                            <div className="p-3 bg-white rounded-lg">
                              <p className="font-medium text-gray-700 mb-2">🔍 Ragionamento:</p>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {aiDiagnosis.strabismo.spiegazione.map((s, i) => (
                                  <li key={i}>• {s}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {aiDiagnosis.strabismo.diagnosi_differenziali?.length > 0 && (
                            <div className="p-3 bg-white rounded-lg">
                              <p className="font-medium text-gray-700 mb-2">⚠️ Diagnosi Differenziali da escludere:</p>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {aiDiagnosis.strabismo.diagnosi_differenziali.map((d, i) => (
                                  <li key={i}>• {d}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {aiDiagnosis.strabismo.terapia_consigliata && (
                            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                              <p className="font-medium text-green-800 mb-2">💊 Terapia Consigliata:</p>
                              <p className="text-sm text-green-700">{aiDiagnosis.strabismo.terapia_consigliata.indicazione_chirurgica}</p>
                              <p className="text-sm text-green-700 font-medium mt-1">{aiDiagnosis.strabismo.terapia_consigliata.intervento}</p>
                              <p className="text-xs text-gray-600 mt-3 italic">
                                {aiDiagnosis.strabismo.terapia_consigliata.avvertenza}
                              </p>
                            </div>
                          )}
                          
                          {aiDiagnosis.pianoOperatorio && (
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <p className="font-medium text-blue-800 mb-2">🔪 Piano Operatorio Calcolato:</p>
                              <p className="text-sm text-blue-700">
                                Deviazione: {aiDiagnosis.pianoOperatorio.deviazione_dp} DP
                              </p>
                              {aiDiagnosis.pianoOperatorio.piano_operatorio?.map((step, i) => (
                                <div key={i} className="mt-2 p-2 bg-white rounded">
                                  <p className="font-medium text-sm">{step.procedura || step.nota}</p>
                                  {step.occhio && <p className="text-xs text-gray-600">Occhio: {step.occhio}</p>}
                                  {step.mm && <p className="text-xs text-gray-600">Mm: {step.mm}</p>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Bottone Ricerca MediSearch */}
                      <button
                        onClick={() => searchMedisearch(aiDiagnosis?.strabismo?.diagnosi_principale || visitData.diagnosi_principale || 'strabismo')}
                        disabled={searchingMedisearch}
                        className="mt-3 w-full py-2 bg-white border border-purple-300 text-purple-700 rounded-lg text-sm hover:bg-purple-50 flex items-center justify-center gap-2"
                      >
                        {searchingMedisearch ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                        Cerca letteratura su MediSearch
                      </button>
                      
                      {aiDiagnosis.medisearch && (
                        <div className="mt-3 p-3 bg-white rounded-lg">
                          <p className="font-medium text-gray-700 mb-2">📚 Da MediSearch:</p>
                          <p className="text-sm text-gray-600">{aiDiagnosis.medisearch.response?.substring(0, 500)}...</p>
                          {aiDiagnosis.medisearch.articles?.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs font-medium text-gray-500">Fonti:</p>
                              {aiDiagnosis.medisearch.articles.slice(0, 3).map((a, i) => (
                                <p key={i} className="text-xs text-blue-600">{a.title}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Campi Diagnosi Manuali */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Diagnosi Principale</label>
                    <input
                      value={visitData.diagnosi_principale}
                      onChange={(e) => handleChange('diagnosi_principale', e.target.value)}
                      placeholder="Es: Esotropia accomodativa, Miopia elevata, Glaucoma sospetto..."
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Terapia Medica</label>
                    <textarea
                      value={visitData.terapia_medica}
                      onChange={(e) => handleChange('terapia_medica', e.target.value)}
                      placeholder="Prescrizione farmaci, colliri, occlusione, occhiali..."
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 text-sm h-24"
                    />
                  </div>
                  
                  <CheckboxField label="Indicazione chirurgica" field="terapia_chirurgica_indicata" />
                  
                  {visitData.terapia_chirurgica_indicata && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Piano Chirurgico</label>
                      <textarea
                        value={visitData.piano_chirurgico}
                        onChange={(e) => handleChange('piano_chirurgico', e.target.value)}
                        placeholder="Descrizione intervento, muscoli coinvolti, mm di recessione/resezione..."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 text-sm h-24"
                      />
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Note</label>
                    <textarea
                      value={visitData.note}
                      onChange={(e) => handleChange('note', e.target.value)}
                      placeholder="Note aggiuntive..."
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 text-sm h-20"
                    />
                  </div>
                  
                  <InputField label="Prossimo Controllo" field="prossimo_controllo" placeholder="3 mesi, 6 mesi, 1 anno..." />
                </div>
              )}
            </div>
            
          </div>
          
          {/* Sidebar - Patient Info */}
          <div className="space-y-4">
            {/* Patient Card */}
            <div className="bg-white rounded-xl shadow-sm p-4 sticky top-20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center">
                  <span className="text-lg font-bold text-sky-700">
                    {patient?.first_name?.[0]}{patient?.last_name?.[0]}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">
                    {patient?.first_name} {patient?.last_name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {patient?.date_of_birth ? `${calculateAge(patient.date_of_birth)} anni` : ''}
                  </p>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Codice Fiscale:</span>
                  <span className="font-medium">{patient?.fiscal_code || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Telefono:</span>
                  <span className="font-medium">{patient?.phone || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Email:</span>
                  <span className="font-medium text-xs">{patient?.email || '-'}</span>
                </div>
              </div>
              
              {/* Quick Actions */}
              <div className="mt-4 pt-4 border-t space-y-2">
                <button 
                  onClick={() => navigate(`/cartella-clinica/${patientId}`)}
                  className="w-full py-2 px-3 bg-sky-50 text-sky-700 rounded-lg text-sm hover:bg-sky-100 flex items-center justify-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Apri Cartella Clinica
                </button>
                
                <button 
                  onClick={() => navigate(`/patient-details/${patientId}`)}
                  className="w-full py-2 px-3 bg-gray-50 text-gray-700 rounded-lg text-sm hover:bg-gray-100 flex items-center justify-center gap-2"
                >
                  <BookOpen className="w-4 h-4" />
                  Visite Precedenti
                </button>
              </div>
            </div>
            
            {/* Alert Allergies */}
            {anamnesi?.allergies?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <span className="font-semibold text-amber-800">Allergie</span>
                </div>
                <ul className="text-sm text-amber-700">
                  {anamnesi.allergies.map((a, i) => (
                    <li key={i}>• {a}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitaOculistica;
